import { Link } from '@tanstack/react-router'
import type { AuditLogDTO } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { Activity, Clock, FileText, Shield, Users } from 'lucide-react'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'

export function AuditLogsTable({
  logs,
  isLoading = false,
  variant = 'table',
  workspaceId,
}: {
  logs: AuditLogDTO[]
  isLoading?: boolean
  variant?: 'table' | 'feed'
  workspaceId?: string
}) {
  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'member':
        return <Users className='h-4 w-4' />
      case 'security':
      case 'auth':
        return <Shield className='h-4 w-4' />
      case 'content':
        return <FileText className='h-4 w-4' />
      default:
        return <Activity className='h-4 w-4' />
    }
  }

  if (variant === 'feed')
    return (
      <ul className='space-y-4'>
        {logs.slice(0, 5).map((log) => (
          <li key={log.id} className='flex items-start gap-2 text-xs'>
            <MemberAvatar
              person={{ name: log.actorName || 'System' }}
              size='xs'
            />
            <div className='min-w-0 flex-1'>
              <p className='break-words'>
                <span className='font-medium'>{log.actorName || 'System'}</span>{' '}
                · {log.action.replace(/[._-]+/g, ' ')}
                {log.targetName && ` · ${log.targetName}`}
              </p>
              <time
                className='text-muted-foreground mt-1 block text-[10px]'
                dateTime={new Date(log.timestamp).toISOString()}
              >
                {formatTimestamp(log.timestamp)}
              </time>
              {workspaceId && (
                <Link
                  to='/workspaces/$workspaceId/settings/audit-logs'
                  params={{ workspaceId }}
                  className='text-cobalt-strong mt-1 inline-block text-[11px] hover:underline'
                >
                  View activity →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    )

  return (
    <div className='border-border bg-card overflow-hidden rounded-sm border [&>div]:overflow-hidden'>
      <Table className='font-body w-full table-fixed'>
        <TableHeader className='bg-surface-subtle [&_tr]:border-border'>
          <TableRow className='hover:bg-surface-subtle h-[40px]'>
            <TableHead className='text-text-subtle w-[116px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              Timestamp
            </TableHead>
            <TableHead className='text-text-subtle w-[100px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              Actor
            </TableHead>
            <TableHead className='text-text-subtle w-[190px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              Action
            </TableHead>
            <TableHead className='text-text-subtle w-[112px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              Category
            </TableHead>
            <TableHead className='text-text-subtle px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              Target
            </TableHead>
            <TableHead className='text-text-subtle w-[108px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
              IP Address
            </TableHead>
            <TableHead className='text-text-subtle w-[104px] px-3 pl-5 text-[10px] font-semibold tracking-[1px] uppercase'>
              Result
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className='py-8 text-center'>
                <div className='text-muted-foreground flex items-center justify-center gap-2'>
                  <Clock className='h-4 w-4 animate-spin' />
                  Loading...
                </div>
              </TableCell>
            </TableRow>
          ) : logs.length > 0 ? (
            logs.map((log) => (
              <TableRow
                key={log.id}
                className='border-border hover:bg-surface-subtle h-[56px]'
              >
                <TableCell className='text-text-subtle truncate px-3 py-3 text-[12px]'>
                  {formatTimestamp(log.timestamp)}
                </TableCell>
                <TableCell className='px-3 py-3'>
                  <div className='flex min-w-0 flex-col'>
                    <span className='text-text-secondary truncate text-[13px] font-medium'>
                      {log.actorName || 'System'}
                    </span>
                    {log.actorType === 'SYSTEM' && (
                      <span className='text-text-subtle truncate text-[12px]'>
                        Automated
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className='px-3 py-3 pl-5'>
                  <span
                    className='bg-surface-subtle text-text-secondary block rounded-sm px-2 py-1 text-[12px] break-words'
                    title={log.action}
                  >
                    {log.action
                      .replace(/^evidence\./, '')
                      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                      .replace(/[._-]+/g, ' ')
                      .replace(/^auth\b/, 'authentication')
                      .replace(/\bapi\b/gi, 'API')
                      .replace(/\b2fa\b/gi, '2FA')
                      .replace(/^./, (letter) => letter.toUpperCase())}
                  </span>
                </TableCell>
                <TableCell className='px-3 py-3'>
                  <Badge variant='outline' className='max-w-full capitalize'>
                    {getCategoryIcon(log.category)}
                    <span className='truncate'>{log.category}</span>
                  </Badge>
                </TableCell>
                <TableCell className='px-3 py-3'>
                  {log.targetName ? (
                    <div className='flex min-w-0 flex-col'>
                      <span className='text-text-secondary line-clamp-2 text-[13px]'>
                        {log.targetName}
                      </span>
                      {log.targetType && (
                        <span className='text-text-subtle truncate text-[12px]'>
                          {log.targetType === 'control_evidence_status'
                            ? 'Test'
                            : log.targetType === 'remediation_ticket'
                              ? 'Finding'
                              : log.targetType === 'control_def'
                                ? 'Control'
                                : log.targetType
                                    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                                    .replace(/[._-]+/g, ' ')
                                    .replace(/^./, (letter) =>
                                      letter.toUpperCase()
                                    )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className='text-text-subtle text-[12px]'>-</span>
                  )}
                </TableCell>
                <TableCell className='text-text-subtle px-3 py-3 font-mono text-[12px]'>
                  {log.actorIp ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='block truncate' tabIndex={0}>
                          {log.actorIp}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className='font-mono'>
                        {log.actorIp}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell className='px-3 py-3'>
                  {log.result === 'SUCCESS' ? (
                    <Badge status='green' type='dot'>
                      Success
                    </Badge>
                  ) : (
                    <Badge status='red' type='dot'>
                      Failed
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={7}
                className='text-muted-foreground py-12 text-center'
              >
                <div className='flex flex-col items-center gap-2'>
                  <Activity className='text-muted-foreground/50 h-12 w-12' />
                  <p className='font-medium'>No audit logs yet</p>
                  <p className='text-sm'>
                    Activity in your organization will appear here
                  </p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
